import { useAuthentication } from '@/composables/useAuthentication';
import { JsonLayerResponse, isJsonLayerResponse } from '@/interfaces/jsonLayerResponse';
import { JsonLayerChartStyle, isJsonLayerChartStyle } from '@/interfaces/json-layer-responses/jsonLayerChartStyle';

interface FetchChartStylesResponse extends JsonLayerResponse {
  chartStyles: any[];
}

function isFetchChartStylesResponse(jsonLayerResponse: JsonLayerResponse): jsonLayerResponse is FetchChartStylesResponse {
  return (
    'chartStyles' in jsonLayerResponse &&
    Array.isArray(jsonLayerResponse.chartStyles)
  );
}

export async function fetchChartStyles(): Promise<JsonLayerChartStyle[]> {
  const { isLoggedIn, chartCode } = useAuthentication();

  if (!isLoggedIn.value) {
    throw new Error('Unable to fetch ChartStyles - user is not logged in.');
  }

  const chartCodeCopy = chartCode.value;
  let jsonLayerResponse;

  try {
    const response = await fetch('/json/api', {
      method: 'POST',
      body: new URLSearchParams({
        cmd: 'get-chartstyles',
        chartCode: chartCodeCopy
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    jsonLayerResponse = await response.json();

    window.sc3WorkbenchLogger.info(`ChartStyles jsonLayerResponse for chartCode: ${chartCodeCopy}.`, jsonLayerResponse);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to fetch ChartStyles for chartCode: ${chartCodeCopy}.`, e);

    throw new Error(`Failed to fetch ChartStyles for chartCode: ${chartCodeCopy}.`, { cause: e });
  }

  if (!isJsonLayerResponse(jsonLayerResponse)) {
    throw new Error(`Failed to fetch ChartStyles for chartCode: ${chartCodeCopy} - jsonLayerResponse does not match JsonLayerResponse interface.`);
  }

  if (!jsonLayerResponse.success) {
    throw new Error(`Failed to fetch ChartStyles for chartCode: ${chartCodeCopy} - jsonLayerResponse.success is false - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }

  if (!isFetchChartStylesResponse(jsonLayerResponse)) {
    throw new Error(`Failed to fetch ChartStyles for chartCode: ${chartCodeCopy} - jsonLayerResponse.success is true but jsonLayerResponse does not match FetchChartStylesResponse interface - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }

  const jsonLayerChartStyles = jsonLayerResponse.chartStyles.filter(isJsonLayerChartStyle);
  const numInvalidJsonLayerChartStyles = jsonLayerResponse.chartStyles.length - jsonLayerChartStyles.length;

  if (numInvalidJsonLayerChartStyles > 0) {
    window.sc3WorkbenchLogger.warn(`json-layer get-chartstyles command returned ${numInvalidJsonLayerChartStyles} invalid ChartStyles (which did not match the JsonLayerChartStyle interface) for chartCode: ${chartCodeCopy}.`);
  }

  return jsonLayerChartStyles;
}
